import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { BookOpen, Menu, X } from 'lucide-react';

const LINKEDIN_URL = 'https://www.linkedin.com/company/r0-hub/';
const REDDIT_URL = 'https://www.reddit.com/u/R0Hub/s/BKa928jxg1';

const trackSocialClick = (platform: string) => {
  if (typeof window !== 'undefined' && (window as any).gtag) {
    (window as any).gtag('event', `${platform} Click`, {
      event_category: 'Social',
      event_label: platform,
    });
  }
};

const SocialIcons = ({ size = 20, className = '' }: { size?: number; className?: string }) => (
  <div className={`flex items-center gap-3 ${className}`}>
    <a
      href={LINKEDIN_URL}
      target="_blank"
      rel="noopener noreferrer"
      onClick={() => trackSocialClick('LinkedIn')}
      className="text-muted-foreground hover:text-primary transition-colors duration-200"
      aria-label="LinkedIn"
    >
      <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
        <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
      </svg>
    </a>
    <a
      href={REDDIT_URL}
      target="_blank"
      rel="noopener noreferrer"
      onClick={() => trackSocialClick('Reddit')}
      className="text-muted-foreground hover:text-primary transition-colors duration-200"
      aria-label="Reddit"
    >
      <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
        <path d="M12 0A12 12 0 000 12a12 12 0 0012 12 12 12 0 0012-12A12 12 0 0012 0zm5.01 4.744c.688 0 1.25.561 1.25 1.249a1.25 1.25 0 01-2.498.056l-2.597-.547-.8 3.747c1.824.07 3.48.632 4.674 1.488.308-.309.73-.491 1.207-.491.968 0 1.754.786 1.754 1.754 0 .716-.435 1.333-1.01 1.614a3.111 3.111 0 01.042.52c0 2.694-3.13 4.87-7.004 4.87-3.874 0-7.004-2.176-7.004-4.87 0-.183.015-.366.043-.534A1.748 1.748 0 014.028 12c0-.968.786-1.754 1.754-1.754.463 0 .898.196 1.207.49 1.207-.883 2.878-1.43 4.744-1.487l.885-4.182a.342.342 0 01.14-.197.35.35 0 01.238-.042l2.906.617a1.214 1.214 0 011.108-.701zM9.25 12C8.561 12 8 12.562 8 13.25c0 .687.561 1.248 1.25 1.248.687 0 1.248-.561 1.248-1.249 0-.688-.561-1.249-1.249-1.249zm5.5 0c-.687 0-1.248.561-1.248 1.25 0 .687.561 1.248 1.249 1.248.688 0 1.249-.561 1.249-1.249 0-.687-.562-1.249-1.25-1.249zm-5.466 3.99a.327.327 0 00-.231.094.33.33 0 000 .463c.842.842 2.484.913 2.961.913.477 0 2.105-.056 2.961-.913a.361.361 0 000-.463.327.327 0 00-.462 0c-.545.533-1.684.73-2.512.73-.828 0-1.979-.196-2.512-.73a.326.326 0 00-.205-.094z" />
      </svg>
    </a>
  </div>
);
import { Button } from '@/components/ui/button';
import { useAuth } from '@/contexts/AuthContext';

interface PublicLayoutProps {
  children: React.ReactNode;
}

export default function PublicLayout({ children }: PublicLayoutProps) {
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { user, isAdmin } = useAuth();

  return (
    <div className="min-h-screen gradient-navy-radial">
      {/* Nav */}
      <nav className="border-b border-border/60 bg-card/95 backdrop-blur-xl sticky top-0 z-50">
        <div className="container flex items-center justify-between h-16">
          <div className="flex items-center gap-6 shrink-0">
            <div className="flex flex-nowrap shrink-0 items-center gap-2.5 cursor-pointer" onClick={() => navigate(user ? '/dashboard' : '/')}>
              <div className="w-9 h-9 shrink-0 rounded-xl gradient-navy flex items-center justify-center shadow-sm" aria-hidden="true">
                <BookOpen className="w-4 h-4 text-primary-foreground" />
              </div>
              <span className="shrink-0 whitespace-nowrap font-display text-xl font-bold text-foreground">R0 Hub</span>
            </div>
            <div className="hidden xl:flex items-center gap-1">
              <Button variant="ghost" size="sm" asChild className="text-muted-foreground hover:text-foreground"><Link to="/about">About Us</Link></Button>
              <Button variant="ghost" size="sm" asChild className="text-muted-foreground hover:text-foreground"><Link to="/exam-guides">Free Guides</Link></Button>
              <Button variant="ghost" size="sm" asChild className="text-muted-foreground hover:text-foreground"><Link to="/pricing">Shop</Link></Button>
              <Button variant="ghost" size="sm" asChild className="text-muted-foreground hover:text-foreground"><Link to="/insights">Insights</Link></Button>
              <Button variant="ghost" size="sm" asChild className="text-muted-foreground hover:text-foreground"><Link to="/contact">Contact</Link></Button>
              {user && isAdmin && <Button variant="ghost" size="sm" asChild className="text-muted-foreground hover:text-foreground"><Link to="/r06-predictor">R06 question predictor</Link></Button>}
            </div>
          </div>
          <div className="flex items-center gap-3">
            <SocialIcons size={18} className="hidden sm:flex" />
            {user ? (
              <Button asChild className="gradient-navy text-primary-foreground hover:opacity-90 shadow-sm font-semibold hidden sm:inline-flex">
                <Link to="/dashboard">Dashboard</Link>
              </Button>
            ) : (
              <>
                <Button variant="ghost" asChild className="text-muted-foreground hover:text-foreground hidden sm:inline-flex"><Link to="/login">Sign In</Link></Button>
                <Button asChild className="gradient-navy text-primary-foreground hover:opacity-90 shadow-sm font-semibold hidden sm:inline-flex">
                  <Link to="/register">Get Started</Link>
                </Button>
              </>
            )}
            <Button variant="ghost" size="sm" onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="xl:hidden text-foreground p-2">
              {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </Button>
          </div>
        </div>
        <AnimatePresence>
          {mobileMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.2 }}
              className="xl:hidden border-t border-border/60 overflow-hidden"
            >
              <div className="flex flex-col gap-1 py-3 px-4">
                <Button variant="ghost" size="sm" asChild className="text-muted-foreground hover:text-foreground justify-start"><Link to="/about" onClick={() => setMobileMenuOpen(false)}>About Us</Link></Button>
                <Button variant="ghost" size="sm" asChild className="text-muted-foreground hover:text-foreground justify-start"><Link to="/exam-guides" onClick={() => setMobileMenuOpen(false)}>Free Guides</Link></Button>
                <Button variant="ghost" size="sm" asChild className="text-muted-foreground hover:text-foreground justify-start"><Link to="/pricing" onClick={() => setMobileMenuOpen(false)}>Shop</Link></Button>
                <Button variant="ghost" size="sm" asChild className="text-muted-foreground hover:text-foreground justify-start"><Link to="/insights" onClick={() => setMobileMenuOpen(false)}>Insights</Link></Button>
                <Button variant="ghost" size="sm" asChild className="text-muted-foreground hover:text-foreground justify-start"><Link to="/contact" onClick={() => setMobileMenuOpen(false)}>Contact</Link></Button>
                {user && isAdmin && <Button variant="ghost" size="sm" asChild className="text-muted-foreground hover:text-foreground justify-start"><Link to="/r06-predictor" onClick={() => setMobileMenuOpen(false)}>R06 question predictor</Link></Button>}
                <div className="flex gap-2 mt-2">
                  {user ? (
                    <Button asChild className="gradient-navy text-primary-foreground hover:opacity-90 shadow-sm font-semibold flex-1"><Link to="/dashboard" onClick={() => setMobileMenuOpen(false)}>Dashboard</Link></Button>
                  ) : (
                    <>
                      <Button variant="ghost" asChild className="text-muted-foreground hover:text-foreground flex-1"><Link to="/login" onClick={() => setMobileMenuOpen(false)}>Sign In</Link></Button>
                      <Button asChild className="gradient-navy text-primary-foreground hover:opacity-90 shadow-sm font-semibold flex-1"><Link to="/register" onClick={() => setMobileMenuOpen(false)}>Get Started</Link></Button>
                    </>
                  )}
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </nav>



      <main>{children}</main>

      {/* Footer */}
      <footer className="border-t border-border/60 bg-card/95" role="contentinfo">
        <div className="container py-10">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-8 mb-8">
            <div>
              <div className="flex flex-nowrap items-center gap-2 mb-4">
                <div className="w-7 h-7 shrink-0 rounded-lg gradient-navy flex items-center justify-center" aria-hidden="true">
                  <BookOpen className="w-3.5 h-3.5 text-primary-foreground" />
                </div>
                <span className="shrink-0 whitespace-nowrap font-display text-sm font-bold text-foreground">R0 Hub</span>
              </div>
              <p className="text-xs text-muted-foreground leading-relaxed">CII R0 exam practice questions for R01 - R06. Pass your financial services exams first time.</p>
              <SocialIcons size={16} className="mt-4" />
            </div>
            <nav aria-label="Modules">
              <p className="text-xs font-semibold text-foreground uppercase tracking-wider mb-3">Modules</p>
              <ul className="space-y-2">
                <li><Link to="/r01-exam-practice" className="text-xs text-muted-foreground hover:text-foreground transition-colors">R01 Practice</Link></li>
                <li><Link to="/r02-exam-practice" className="text-xs text-muted-foreground hover:text-foreground transition-colors">R02 Practice</Link></li>
                <li><Link to="/r03-exam-practice" className="text-xs text-muted-foreground hover:text-foreground transition-colors">R03 Practice</Link></li>
                <li><Link to="/r04-exam-practice" className="text-xs text-muted-foreground hover:text-foreground transition-colors">R04 Practice</Link></li>
                <li><Link to="/r05-exam-practice" className="text-xs text-muted-foreground hover:text-foreground transition-colors">R05 Practice</Link></li>
                <li><Link to="/r06-exam-practice" className="text-xs text-muted-foreground hover:text-foreground transition-colors">R06 Practice</Link></li>
              </ul>
            </nav>
            <nav aria-label="Resources">
              <p className="text-xs font-semibold text-foreground uppercase tracking-wider mb-3">Resources</p>
              <ul className="space-y-2">
                <li><Link to="/insights" className="text-xs text-muted-foreground hover:text-foreground transition-colors">Insights & Tips</Link></li>
                <li><Link to="/exam-guides" className="text-xs text-muted-foreground hover:text-foreground transition-colors">Free Guides</Link></li>
                <li><Link to="/cii-exam-dates" className="text-xs text-muted-foreground hover:text-foreground transition-colors">CII Exam Dates</Link></li>
                <li><Link to="/pricing" className="text-xs text-muted-foreground hover:text-foreground transition-colors">Pricing & Plans</Link></li>
              </ul>
            </nav>
            <nav aria-label="Company">
              <p className="text-xs font-semibold text-foreground uppercase tracking-wider mb-3">Company</p>
              <ul className="space-y-2">
                <li><Link to="/about" className="text-xs text-muted-foreground hover:text-foreground transition-colors">About R0 Hub</Link></li>
                <li><Link to="/contact" className="text-xs text-muted-foreground hover:text-foreground transition-colors">Contact Us</Link></li>
                <li><Link to="/terms" className="text-xs text-muted-foreground hover:text-foreground transition-colors">Terms & Conditions</Link></li>
                <li><Link to="/privacy" className="text-xs text-muted-foreground hover:text-foreground transition-colors">Privacy Policy</Link></li>
              </ul>
            </nav>
          </div>
          <div className="border-t border-border/40 pt-6 flex flex-col sm:flex-row items-center justify-between gap-3">
            <p className="text-xs text-muted-foreground">© 2026 R0 Hub. All rights reserved. Operated by Chilbury Ventures Ltd.</p>
          </div>
        </div>
        <p className="text-[10px] text-muted-foreground/50 text-center pb-6 max-w-5xl mx-auto leading-relaxed px-4">
          This website is an independent revision resource and is not affiliated with, endorsed by, or approved by the Chartered Insurance Institute (CII). All content, including questions and explanations, is original and provided for educational purposes only. R0 Hub accepts no liability for the accuracy or completeness of the content provided. While every effort has been made to ensure that all information and questions are correct at the time of publication, no guarantee is given, and users should not rely solely on this content.
        </p>
      </footer>
    </div>
  );
}
